import { Box, Typography } from '@mui/material';
import React from 'react';
import StyledModal from '../../styles/StyledModal';
import ImageCarousel from '../../template/ImageCarousel';
import LeftRightList from '../../template/LeftRightList';
import StandardHeaderText from '../../template/StandardHeaderText';
import Table from '../../template/Table';
import { articleApprovalTableGuideData } from './articleApprovalTableGuideData';

interface ArticleRefinementHelpModalProps {
  open: boolean;
  onClose: () => void;
}

export default function ArticleRefinementHelpModal({
  open,
  onClose,
}: ArticleRefinementHelpModalProps) {
  return (
    <StyledModal
      open={open}
      onClose={onClose}
      title="AI Refinement Guide"
      fullWidth
      maxWidth="lg"
      PaperProps={{
        sx: {
          maxHeight: '80vh',
          height: '80vh',
        },
      }}
    >
      <Box
        sx={{
          p: 2,
          height: '100%',
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box sx={{ mb: 3, p: 2, backgroundColor: '#f0f8ff', borderRadius: 1 }}>
          <Typography variant="h6" sx={{ color: '#078282', mb: 1 }}>
            How to Request Refinements
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            The AI can understand and modify financial news articles. You can request specific
            changes, ask for new content sections, or modify existing content. The system supports
            both simple text requests and detailed structural changes.
          </Typography>
          <Typography variant="body2" sx={{ fontSize: '0.875rem', color: '#666' }}>
            <strong>Examples:</strong> "Add a risk assessment section", "Make the summary more
            concise", "Convert the project details into a table"
          </Typography>
        </Box>

        <Typography variant="h6" sx={{ mb: 2, color: '#078282' }}>
          Available Content Types
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: 3,
          }}
        >
          <Box
            sx={{
              border: '1px solid #e0e0e0',
              borderRadius: '8px',
              p: 2,
              backgroundColor: '#fafafa',
            }}
          >
            <Typography
              variant="h6"
              sx={{ fontSize: '1rem', fontWeight: 600, mb: 1, color: '#078282' }}
            >
              Text Section
            </Typography>
            <Typography variant="body2" sx={{ fontSize: '0.8rem', color: '#666', mb: 2 }}>
              A heading with content below (text, lists, or nested lists)
            </Typography>
            <Box sx={{ zoom: 0.4, width: 'fit-content' }}>
              <StandardHeaderText
                id="555f27c9-749b-4962-b711-7eef85270970"
                editMode={false}
                heading={{
                  id: '8c7f41b7-7bde-4565-adef-828ab86dcf3a',
                  content: 'Investment Snapshot',
                  tag: '',
                  subHeader: null,
                }}
                content={{
                  id: 'a13ef8a1-8b01-4f2b-8410-e529bf0d8d22',
                  content:
                    '<p>Forecast IRR: 13.0% (pre-tax; net of fees)</p>\n<p>Forecast Money Multiple: 1.11x</p>',
                }}
                tone="tone1"
                firstElement={true}
                lastElement={false}
                showEditButtons={false}
              />
            </Box>
          </Box>

          <Box
            sx={{
              border: '1px solid #e0e0e0',
              borderRadius: '8px',
              p: 2,
              backgroundColor: '#fafafa',
            }}
          >
            <Typography
              variant="h6"
              sx={{ fontSize: '1rem', fontWeight: 600, mb: 1, color: '#078282' }}
            >
              Structured List
            </Typography>
            <Typography variant="body2" sx={{ fontSize: '0.8rem', color: '#666', mb: 2 }}>
              Two-column layout with titles on left, descriptions on right
            </Typography>
            <Box sx={{ zoom: 0.4, width: 'fit-content' }}>
              <LeftRightList
                id="94e7a7cc-9368-4266-becf-a0b9577c10a9"
                editMode={false}
                heading={{
                  id: 'e3dcc755-19ad-45df-9907-a43f69636592',
                  content: 'Risks and Mitigants',
                  tag: 'tag placeholder',
                  subHeader: null,
                }}
                items={[
                  {
                    heading: {
                      id: '07e70965-d1bb-4811-8ec6-505e7d1c0b28',
                      content: 'Development Risk',
                    },
                    text: {
                      id: '6ac2ebcd-e620-4862-b9e9-fd77cb0796d9',
                      content:
                        'The civil contract is executed with Winslow at a fixed-price and the Borrower has allowed for a 5% contingency.',
                    },
                  },
                  {
                    heading: {
                      id: '7d6a1f3d-bff4-48ee-b2bb-dac3e6c90ce3',
                      content: 'Repayment Risk',
                    },
                    text: {
                      id: 'af7fe846-4a88-47f1-9951-4a51d26c011e',
                      content:
                        'As a condition precedent to drawdown of the Facility, the Borrower must ensure at least $7,460,000 in net qualifying pre-sales.',
                    },
                  },
                ]}
                firstElement={false}
                lastElement={false}
                showEditButtons={false}
              />
            </Box>
          </Box>

          <Box
            sx={{
              border: '1px solid #e0e0e0',
              borderRadius: '8px',
              p: 2,
              backgroundColor: '#fafafa',
            }}
          >
            <Typography
              variant="h6"
              sx={{ fontSize: '1rem', fontWeight: 600, mb: 1, color: '#078282' }}
            >
              Image Cards
            </Typography>
            <Typography variant="body2" sx={{ fontSize: '0.8rem', color: '#666', mb: 2 }}>
              Multiple images with descriptions
            </Typography>
            <Box sx={{ zoom: 0.4, width: 'fit-content' }}>
              <ImageCarousel
                modelId="guide-preview"
                modelType="Guide"
                id="107c452d-52ed-4b28-b4be-bc2200f9a4d3"
                editMode={false}
                heading={{
                  id: 'ef8ddee8-6e97-48dc-a803-b09da924bd1a',
                  content: 'Property Aerial',
                  tag: 'tag placeholder',
                  subHeader: null,
                }}
                items={[
                  {
                    heading: {
                      id: '1001d5bb-a63e-4731-a27b-ef4d500d1b0b',
                      content: 'Parent Project',
                    },
                    text: {
                      id: '481294f4-0970-44a3-aa2a-dfcbfc800229',
                      content: '<p>Aerial satellite image</p>',
                    },
                    imageUrl: {
                      id: '27d5aac7-2835-4c31-8914-2b99a6d89d0c',
                      url: '/assets/carousel_sample_1.jpg',
                    },
                    altText: {
                      id: '4e3118f1-6307-4da7-bf77-c20cb354d00d',
                      content: 'Sample Image 1',
                    },
                    summaryImage: false,
                  },
                  {
                    heading: {
                      id: 'a80b0a15-4253-4556-9c53-ceb425d391a3',
                      content: 'Parent Project Render',
                    },
                    text: {
                      id: '8d27c3e1-7b7c-49af-81ae-fcdd4c0b0128',
                      content:
                        '<p>A first mortgage, senior debt facility up to $12.27mn over a 12-month investment term.</p>',
                    },
                    imageUrl: {
                      id: 'd9d3003e-3983-45ed-b357-07daf63895b1',
                      url: '/assets/carousel_sample_2.jpg',
                    },
                    altText: {
                      id: 'b19981a2-4dda-44f4-b3fc-ce8fbcf8bb2b',
                      content: 'Sample Image 2',
                    },
                    summaryImage: true,
                  },
                ]}
              />
            </Box>
          </Box>

          <Box
            sx={{
              border: '1px solid #e0e0e0',
              borderRadius: '8px',
              p: 2,
              backgroundColor: '#fafafa',
            }}
          >
            <Typography
              variant="h6"
              sx={{ fontSize: '1rem', fontWeight: 600, mb: 1, color: '#078282' }}
            >
              Table
            </Typography>
            <Typography variant="body2" sx={{ fontSize: '0.8rem', color: '#666', mb: 2 }}>
              Tabular data in rows and columns
            </Typography>
            <Box sx={{ zoom: 0.4, overflow: 'auto' }}>
              <Table
                id={articleApprovalTableGuideData.id}
                editMode={false}
                heading={articleApprovalTableGuideData.heading}
                rows={articleApprovalTableGuideData.rows}
                firstElement={false}
                lastElement={false}
                showEditButtons={false}
              />
            </Box>
          </Box>
        </Box>

        <Box sx={{ mt: 3, p: 2, backgroundColor: '#f9f9f9', borderRadius: 1 }}>
          <Typography variant="h6" sx={{ mb: 2, color: '#078282' }}>
            Request Examples
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: 2,
            }}
          >
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Content Modifications
              </Typography>
              <Typography variant="body2" sx={{ fontSize: '0.875rem', color: '#666' }}>
                • "Make the summary more concise"
                <br />
                • "Add more detail to the risk assessment"
                <br />• "Rewrite the investment highlights section"
              </Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Structural Changes
              </Typography>
              <Typography variant="body2" sx={{ fontSize: '0.875rem', color: '#666' }}>
                • "Convert the project details into a table"
                <br />
                • "Create a structured list for the team members"
                <br />• "Add a new section about market conditions"
              </Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Format Requests
              </Typography>
              <Typography variant="body2" sx={{ fontSize: '0.875rem', color: '#666' }}>
                • "Use bullet points for the key features"
                <br />
                • "Organize the financial data in a table"
                <br />• "Create a two-column layout for risks and mitigants"
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            mt: 3,
            p: 2,
            backgroundColor: '#fff3cd',
            borderRadius: 1,
            border: '1px solid #ffeaa7',
          }}
        >
          <Typography variant="h6" sx={{ mb: 1, color: '#856404' }}>
            AI Model Information
          </Typography>
          <Typography variant="body2" sx={{ fontSize: '0.875rem', color: '#856404' }}>
            The system uses advanced AI models (Gemini and Mistral) to understand and refine
            financial content. It can process complex PDF documents, extract structured data, and
            generate professional financial news articles with proper formatting and organization.
          </Typography>
        </Box>
      </Box>
    </StyledModal>
  );
}
